import React, { useState, useEffect } from "react";
import { validateField } from "../utils/validationRules";
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  ExternalLink,
  Loader,
  Eye,
  Search,
  Code,
  Copy,
  X,
  ChevronDown,
} from "lucide-react";
import { apiCall } from "../api";
import "./ExtractionComparison.css";

const ExtractionComparison = ({ workOrderId }) => {
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showRawJson, setShowRawJson] = useState(false);
  const [copiedTab, setCopiedTab] = useState(null);
  const [viewMode, setViewMode] = useState("mapped");
  const [showProviderJson, setShowProviderJson] = useState(false);
  const [providerResponse, setProviderResponse] = useState(null);
  const [providerLoading, setProviderLoading] = useState(false);
  const [fieldMappings, setFieldMappings] = useState([]);
  const [enableGLIB, setEnableGLIB] = useState(true);
  const [aiProvider, setAiProvider] = useState("openrouter");
  const [refreshingGlib, setRefreshingGlib] = useState(false);

  // New state for file/form selection
  const [splitFiles, setSplitFiles] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // Fetch split files for dropdown
  const fetchSplitFiles = async () => {
    setLoadingFiles(true);
    try {
      const response = await fetch(
        `/api/work-orders/${workOrderId}/split-files`,
      );
      if (response.ok) {
        const files = await response.json();
        setSplitFiles(files || []);
        // Auto-select first file if none selected
        if (files && files.length > 0 && !selectedFileId) {
          setSelectedFileId(files[0]._id);
        }
      }
    } catch (err) {
      console.error("Failed to fetch split files:", err);
    } finally {
      setLoadingFiles(false);
    }
  };

  // Fetch comparison data for selected file
  const fetchComparisonForFile = async (fileId) => {
    if (!fileId) {
      // Fall back to original comparison endpoint
      fetchComparison();
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `/api/work-orders/${workOrderId}/comparison/${fileId}`,
      );
      if (response.ok) {
        const data = await response.json();
        setComparison(data);
        if (
          data.local?.status === "completed" &&
          (data.glib?.status === "completed" ||
            data.glib?.status === "failed" ||
            data.glib?.status === "not_started")
        ) {
          setAutoRefresh(false);
        }
      } else {
        setError("Failed to fetch comparison data for selected file");
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection change
  const handleFileChange = (e) => {
    const fileId = e.target.value;
    setSelectedFileId(fileId);
    fetchComparisonForFile(fileId);
  };

  // Fetch raw provider response
  const fetchProviderResponse = async () => {
    setProviderLoading(true);
    try {
      const response = await fetch(
        `/api/work-orders/${workOrderId}/provider-response`,
      );
      if (response.ok) {
        const data = await response.json();
        setProviderResponse(data);
        setShowProviderJson(true);
      } else {
        alert("No provider response found for this work order");
      }
    } catch (err) {
      alert("Failed to fetch provider response: " + err.message);
    } finally {
      setProviderLoading(false);
    }
  };

  // Refresh GLIB data from portal (re-fetch for previously uploaded files)
  const refreshGlibData = async () => {
    setRefreshingGlib(true);
    try {
      const response = await fetch(
        `/api/work-orders/${workOrderId}/glib/refresh`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        },
      );
      const data = await response.json();
      if (data.success) {
        alert(
          `GLIB data refreshed! ${data.fieldsExtracted || 0} fields extracted.`,
        );
        // Re-fetch comparison data
        if (selectedFileId) {
          fetchComparisonForFile(selectedFileId);
        } else {
          fetchComparison();
        }
      } else {
        alert(`Failed to refresh: ${data.error || data.message}`);
      }
    } catch (err) {
      alert("Failed to refresh GLIB data: " + err.message);
    } finally {
      setRefreshingGlib(false);
    }
  };

  // Load field mappings from JSON file
  useEffect(() => {
    const loadMappings = async () => {
      try {
        const response = await fetch("/api/field-mappings");
        if (response.ok) {
          const data = await response.json();
          setFieldMappings(data.mappings || []);
        }
      } catch (err) {
        console.error("Failed to load field mappings:", err);
      }
    };
    loadMappings();
  }, []);

  // Load system settings to check if GLIB is enabled and get AI provider
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await apiCall("/api/settings");
        if (data && typeof data.enableGLIB === "boolean") {
          setEnableGLIB(data.enableGLIB);
          console.log(
            "[ExtractionComparison] GLIB setting:",
            data.enableGLIB ? "ENABLED" : "DISABLED",
          );
        }
        if (data && data.aiProvider) {
          setAiProvider(data.aiProvider);
          console.log("[ExtractionComparison] AI Provider:", data.aiProvider);
        }
      } catch (err) {
        console.error("Failed to load settings:", err);
      }
    };
    loadSettings();
  }, []);

  // Fetch split files on mount
  useEffect(() => {
    fetchSplitFiles();
  }, [workOrderId]);

  // Fetch comparison when selected file changes
  useEffect(() => {
    if (selectedFileId) {
      fetchComparisonForFile(selectedFileId);
    } else {
      fetchComparison();
    }
  }, [selectedFileId]);

  const fetchComparison = async () => {
    try {
      const data = await apiCall(`/api/work-orders/${workOrderId}/comparison`);
      if (data) {
        setComparison(data);
        if (
          data.local?.status === "completed" &&
          (data.glib?.status === "completed" ||
            data.glib?.status === "failed" ||
            data.glib?.status === "not_started")
        ) {
          setAutoRefresh(false);
        }
      } else {
        setError("Failed to fetch comparison data");
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchPdfForViewing = async () => {
    try {
      // Use selected file if available, otherwise use first file
      const fileIdToUse =
        selectedFileId || (splitFiles.length > 0 ? splitFiles[0]._id : null);

      if (fileIdToUse) {
        const pdfRes = await fetch(
          `/api/work-orders/${workOrderId}/files/${fileIdToUse}/download`,
        );
        if (pdfRes.ok) {
          const blob = await pdfRes.blob();
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
          setShowPdfViewer(true);
          return;
        }
      }

      // Fallback to first available file
      const filesRes = await fetch(`/api/work-orders/${workOrderId}/files`);
      if (filesRes.ok) {
        const files = await filesRes.json();
        if (files && files.length > 0) {
          const fileId = files[0]._id;
          const pdfRes = await fetch(
            `/api/work-orders/${workOrderId}/files/${fileId}/download`,
          );
          if (pdfRes.ok) {
            const blob = await pdfRes.blob();
            const url = URL.createObjectURL(blob);
            setPdfUrl(url);
            setShowPdfViewer(true);
          }
        }
      }
    } catch (err) {
      console.error("Failed to load PDF:", err);
    }
  };

  useEffect(() => {
    if (!selectedFileId) {
      fetchComparison();
    }
    // Auto-load PDF for inline view
    if (!pdfUrl) {
      // Load PDF without showing modal
      (async () => {
        try {
          const fileIdToUse = selectedFileId || null;
          if (fileIdToUse) {
            const pdfRes = await fetch(
              `/api/work-orders/${workOrderId}/files/${fileIdToUse}/download`,
            );
            if (pdfRes.ok) {
              const blob = await pdfRes.blob();
              setPdfUrl(URL.createObjectURL(blob));
              return;
            }
          }
          // Fallback: try first file from work order
          const filesRes = await fetch(`/api/work-orders/${workOrderId}/files`);
          if (filesRes.ok) {
            const files = await filesRes.json();
            if (files.length > 0) {
              const pdfRes = await fetch(
                `/api/work-orders/${workOrderId}/files/${files[0]._id}/download`,
              );
              if (pdfRes.ok) {
                const blob = await pdfRes.blob();
                setPdfUrl(URL.createObjectURL(blob));
              }
            }
          }
        } catch (err) {
          console.warn("Failed to auto-load PDF:", err);
        }
      })();
    }
  }, [workOrderId]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchComparison, 3000);
    return () => clearInterval(interval);
  }, [autoRefresh, workOrderId]);

  // Normalize key for matching - more aggressive normalization
  const normalizeKey = (key) => {
    return key
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/-/g, "_")
      .replace(/[^\w_]/g, "")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "")
      .trim();
  };

  // Flatten nested object to key-value pairs with original key preservation
  const flattenObject = (
    obj,
    prefix = "",
    isGemma = false,
    originalPrefix = "",
  ) => {
    const result = [];
    if (!obj || typeof obj !== "object") return result;

    for (const [key, value] of Object.entries(obj)) {
      // Skip metadata
      if (key === "Form Type" || key === "form_type") continue;

      const originalKey = key;

      // For Gemma, strip form type wrapper keys
      if (isGemma && !prefix) {
        if (
          key.includes("_Form") ||
          key.includes("Application") ||
          key.includes("Registration")
        ) {
          if (
            typeof value === "object" &&
            value !== null &&
            !Array.isArray(value)
          ) {
            result.push(...flattenObject(value, "", isGemma, key));
            continue;
          }
        }
        // Handle dynamic file prefixes like "164581705_CommonApplicationForm"
        if (/^\d+_[A-Za-z]+/.test(key)) {
          if (
            typeof value === "object" &&
            value !== null &&
            !Array.isArray(value)
          ) {
            result.push(...flattenObject(value, "", isGemma, key));
            continue;
          }
        }
      }

      // Clean key - strip section numbers for Gemma
      let cleanKey = key;
      if (isGemma) {
        cleanKey = key.replace(/^\d+[a-z]?_/, "");
      }

      const fullKey = prefix ? `${prefix}.${cleanKey}` : cleanKey;

      if (Array.isArray(value)) {
        // Handle arrays with index notation
        value.forEach((item, idx) => {
          if (typeof item === "object" && item !== null) {
            for (const [itemKey, itemVal] of Object.entries(item)) {
              const arrayKey = `${fullKey}.${idx}.${itemKey}`;
              let displayValue = itemVal;
              if (typeof itemVal === "boolean")
                displayValue = itemVal ? "Yes" : "No";
              else if (
                itemVal === null ||
                itemVal === undefined ||
                itemVal === ""
              )
                displayValue = "-";
              result.push({
                key: arrayKey,
                value: String(displayValue),
                isFromArray: true,
                arrayIndex: idx,
                originalKey: itemKey,
              });
            }
          } else {
            let displayValue = item;
            if (typeof item === "boolean") displayValue = item ? "Yes" : "No";
            else if (item === null || item === undefined || item === "")
              displayValue = "-";
            result.push({
              key: `${fullKey}.${idx}`,
              value: String(displayValue),
              isFromArray: true,
              arrayIndex: idx,
            });
          }
        });
      } else if (typeof value === "object" && value !== null) {
        // Check for checkbox objects (all boolean values)
        const entries = Object.entries(value);
        const allBooleans =
          entries.length > 0 &&
          entries.every(
            ([, v]) => typeof v === "boolean" || v === null || v === "",
          );
        const hasTrueValue = entries.some(([, v]) => v === true);

        if (allBooleans && hasTrueValue) {
          const selectedKeys = entries
            .filter(([, v]) => v === true)
            .map(([k]) =>
              k.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
            );
          result.push({
            key: fullKey,
            value: selectedKeys.join(", "),
            isCheckbox: true,
            originalKey,
          });
        } else if (!allBooleans) {
          result.push(
            ...flattenObject(value, fullKey, isGemma, originalPrefix),
          );
        }
      } else {
        let displayValue = value;
        if (typeof value === "boolean") displayValue = value ? "Yes" : "No";
        else if (value === null || value === undefined || value === "")
          displayValue = "-";
        result.push({ key: fullKey, value: String(displayValue), originalKey });
      }
    }
    return result;
  };

  // Find value in flattened data by matching path parts
  const findValueByPath = (flatData, pathSpec, sourceType = "any") => {
    if (!pathSpec) return null;

    // Handle combined paths like "first_name + .middle_name + .last_name"
    if (pathSpec.includes(" + ")) {
      const parts = pathSpec
        .split(" + ")
        .map((p) => p.trim().replace(/^\./, ""));
      const values = [];
      // Get base path from first part
      const firstPart = parts[0];
      const basePath = firstPart.includes(".")
        ? firstPart.substring(0, firstPart.lastIndexOf("."))
        : "";

      for (const part of parts) {
        // If part starts with '.', it's relative to base path
        let fullPath = part;
        if (!part.includes(".") && basePath) {
          fullPath = `${basePath}.${part}`;
        }
        const match = findValueByPath(flatData, fullPath, sourceType);
        if (
          match &&
          match.value &&
          match.value !== "-" &&
          match.value !== "null"
        ) {
          values.push(match.value);
        }
      }
      return values.length > 0
        ? { value: values.join(" ").trim(), key: pathSpec }
        : null;
    }

    // Get the target key parts
    const targetParts = pathSpec.split(".");
    const targetLast = normalizeKey(targetParts[targetParts.length - 1]);

    // Also try with common typos/variations
    const targetVariants = [
      targetLast,
      targetLast.replace("applicant", "appiicant"), // Handle GLIB typo
      targetLast.replace("appiicant", "applicant"),
    ];

    // First try exact match by full path ending
    for (const item of flatData) {
      const fullNormalized = normalizeKey(item.key);
      const targetNormalized = normalizeKey(pathSpec);
      if (
        fullNormalized === targetNormalized ||
        fullNormalized.endsWith(targetNormalized)
      ) {
        return { value: item.value, key: item.key };
      }
    }

    // Try matching by last part with context
    for (const item of flatData) {
      const itemParts = item.key.split(".");
      const itemLast = normalizeKey(itemParts[itemParts.length - 1]);

      for (const variant of targetVariants) {
        if (itemLast === variant) {
          // Check if parent paths also match somewhat
          if (targetParts.length > 1 && itemParts.length > 1) {
            const targetParent = normalizeKey(
              targetParts[targetParts.length - 2],
            );
            const itemParent = normalizeKey(itemParts[itemParts.length - 2]);
            if (
              targetParent === itemParent ||
              itemParent.includes(targetParent) ||
              targetParent.includes(itemParent)
            ) {
              return { value: item.value, key: item.key };
            }
          }
          // If just single key, return match
          if (targetParts.length === 1) {
            return { value: item.value, key: item.key };
          }
        }
      }
    }

    // Fallback: match by just the last part
    for (const item of flatData) {
      const itemParts = item.key.split(".");
      const itemLast = normalizeKey(itemParts[itemParts.length - 1]);

      for (const variant of targetVariants) {
        if (itemLast === variant) {
          return { value: item.value, key: item.key };
        }
      }
    }

    return null;
  };

  // Get mapped comparison using field_mappings.json
  const getMappedComparison = () => {
    const glibData = comparison?.glib?.data || {};
    const gemmaData = comparison?.local?.data || {};

    // Flatten both objects - get auxiliary_document for GLIB
    const auxDoc = glibData?.auxiliary_document || glibData;
    const flatGlib = flattenObject(auxDoc, "", false);
    const flatGemma = flattenObject(gemmaData, "", true);

    // Debug logging
    console.log("Flat GLIB keys:", flatGlib.map((f) => f.key).slice(0, 20));
    console.log("Flat Gemma keys:", flatGemma.map((f) => f.key).slice(0, 20));

    // Filter out metadata
    const filteredGlib = flatGlib.filter(
      (item) =>
        !item.key.includes("field_confidence") &&
        !item.key.includes("app_verifications") &&
        !item.key.toLowerCase().includes("form_type"),
    );
    const filteredGemma = flatGemma.filter(
      (item) => !item.key.toLowerCase().includes("form_type"),
    );

    const rows = [];
    const usedGlibKeys = new Set();
    const usedGemmaKeys = new Set();

    // Process mappings from JSON
    for (const mapping of fieldMappings) {
      // Extract key from glib path (remove "auxiliary_document." prefix)
      let glibKey = mapping.glib
        ? mapping.glib.replace(/^auxiliary_document\./, "")
        : "";

      // Handle GLIB keys with spaces (like "First Applicant Gender")
      // These need special handling - look for exact match first

      // Extract key from gemma path (remove form type prefix and section numbers)
      let gemmaKey = mapping.gemma || "";
      gemmaKey = gemmaKey.replace(
        /^(Common_Application_Form|SIP_Registration_OTM_Debit_Mandate_Form)\./,
        "",
      );
      gemmaKey = gemmaKey.replace(/^\d+[a-z]?_/, "");

      const glibMatch = findValueByPath(filteredGlib, glibKey, "glib");
      const gemmaMatch = findValueByPath(filteredGemma, gemmaKey, "gemma");

      if (glibMatch) usedGlibKeys.add(glibMatch.key);
      if (gemmaMatch) usedGemmaKeys.add(gemmaMatch.key);

      const glibVal = glibMatch?.value || "-";
      const gemmaVal = gemmaMatch?.value || "-";

      // Only add if at least one has a meaningful value
      const glibHasValue =
        glibVal !== "-" &&
        glibVal !== "null" &&
        glibVal !== "" &&
        glibVal !== "Not Selected";
      const gemmaHasValue =
        gemmaVal !== "-" &&
        gemmaVal !== "null" &&
        gemmaVal !== "" &&
        gemmaVal !== "No";

      if (glibHasValue || gemmaHasValue) {
        // Determine match status
        let status = "both_empty";
        const glibNorm = glibVal
          .toLowerCase()
          .replace(/[,\/\-=\s]+/g, "")
          .trim();
        const gemmaNorm = gemmaVal
          .toLowerCase()
          .replace(/[,\/\-=\s]+/g, "")
          .trim();

        if (
          glibNorm &&
          glibNorm !== "-" &&
          gemmaNorm &&
          gemmaNorm !== "-" &&
          glibNorm !== "notselected" &&
          gemmaNorm !== "no"
        ) {
          if (
            glibNorm === gemmaNorm ||
            glibNorm.includes(gemmaNorm) ||
            gemmaNorm.includes(glibNorm)
          ) {
            status = "match";
          } else {
            status = "mismatch";
          }
        } else if (glibHasValue && glibNorm !== "notselected") {
          status = "glib_only";
        } else if (gemmaHasValue && gemmaNorm !== "no") {
          status = "gemma_only";
        }

        rows.push({
          display: mapping.field,
          glibValue: glibVal === "null" ? "-" : glibVal,
          gemmaValue: gemmaVal === "null" ? "-" : gemmaVal,
          status,
          glibPath: glibKey,
          gemmaPath: gemmaKey,
        });
      }
    }

    // Add remaining unmatched GEMMA fields
    for (const item of filteredGemma) {
      if (
        !usedGemmaKeys.has(item.key) &&
        item.value &&
        item.value !== "-" &&
        item.value !== "" &&
        item.value !== "No" &&
        item.value !== "null"
      ) {
        const displayName = item.key
          .split(".")
          .pop()
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase());
        // Avoid duplicates
        if (
          !rows.find(
            (r) => normalizeKey(r.display) === normalizeKey(displayName),
          )
        ) {
          rows.push({
            display: displayName,
            glibValue: "-",
            gemmaValue: item.value,
            status: "gemma_only",
            gemmaPath: item.key,
          });
        }
      }
    }

    // Add remaining unmatched GLIB fields
    for (const item of filteredGlib) {
      if (
        !usedGlibKeys.has(item.key) &&
        item.value &&
        item.value !== "-" &&
        item.value !== "" &&
        item.value !== "Not Selected" &&
        item.value !== "null"
      ) {
        const displayName = item.key
          .split(".")
          .pop()
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase());
        // Avoid duplicates
        if (
          !rows.find(
            (r) => normalizeKey(r.display) === normalizeKey(displayName),
          )
        ) {
          rows.push({
            display: displayName,
            glibValue: item.value,
            gemmaValue: "-",
            status: "glib_only",
            glibPath: item.key,
          });
        }
      }
    }

    // Sort: matches first, then mismatches, then single-source
    const order = {
      match: 0,
      mismatch: 1,
      glib_only: 2,
      gemma_only: 3,
      both_empty: 4,
    };
    rows.sort((a, b) => order[a.status] - order[b.status]);

    return rows;
  };

  const filterRows = (rows) => {
    if (!searchTerm.trim()) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter(
      (r) =>
        r.display.toLowerCase().includes(term) ||
        r.glibValue.toLowerCase().includes(term) ||
        r.gemmaValue.toLowerCase().includes(term),
    );
  };

  // Get display name for AI provider
  const getProviderDisplayName = () => {
    switch (aiProvider) {
      case "gemini":
        return "Google Gemini";
      case "bedrock":
      case "bedrock-vl":
        return aiProvider === "bedrock-vl" ? "Bedrock VL-235B" : "Bedrock 32B";
      case "openrouter":
      default:
        return "OpenRouter";
    }
  };

  // Get short provider name for badges
  const getProviderBadgeName = () => {
    switch (aiProvider) {
      case "gemini":
        return "Gemini";
      case "bedrock":
      case "bedrock-vl":
        return aiProvider === "bedrock-vl" ? "Bedrock-VL" : "Bedrock";
      case "openrouter":
      default:
        return "Provider";
    }
  };

  // Get only local extraction data when GLIB is disabled
  const getLocalOnlyComparison = () => {
    const gemmaData = comparison?.local?.data || {};
    const flatGemma = flattenObject(gemmaData, "", true);

    const filteredGemma = flatGemma.filter(
      (item) => !item.key.toLowerCase().includes("form_type"),
    );

    const rows = [];

    // Add all Gemma fields with values
    for (const item of filteredGemma) {
      if (
        item.value &&
        item.value !== "-" &&
        item.value !== "" &&
        item.value !== "No" &&
        item.value !== "null"
      ) {
        const displayName = item.key
          .split(".")
          .pop()
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase());
        // Avoid duplicates
        if (
          !rows.find(
            (r) => normalizeKey(r.display) === normalizeKey(displayName),
          )
        ) {
          rows.push({
            display: displayName,
            gemmaValue: item.value,
            gemmaPath: item.key,
          });
        }
      }
    }

    return rows;
  };

  const copyToClipboard = (text, tab) => {
    navigator.clipboard.writeText(text);
    setCopiedTab(tab);
    setTimeout(() => setCopiedTab(null), 2000);
  };

  if (loading) {
    return (
      <div className="comparison-loading">
        <Loader size={32} className="spin" />
        <p>Loading comparison data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="comparison-error">
        <XCircle size={24} />
        <p>{error}</p>
        <button
          onClick={() => {
            setError("");
            setLoading(true);
            fetchComparison();
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const mappedRows = getMappedComparison();
  const filteredRows = filterRows(mappedRows);
  const stats = {
    total: mappedRows.length,
    matches: mappedRows.filter((r) => r.status === "match").length,
    mismatches: mappedRows.filter((r) => r.status === "mismatch").length,
    glibOnly: mappedRows.filter((r) => r.status === "glib_only").length,
    gemmaOnly: mappedRows.filter((r) => r.status === "gemma_only").length,
  };

  return (
    <div className="extraction-comparison-v2">
      {loading && !comparison && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 20px",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <Loader size={32} className="spin" style={{ color: "#3182ce" }} />
          <span style={{ color: "#718096", fontSize: 14 }}>
            Loading comparison data...
          </span>
        </div>
      )}
      <div className="comp-header">
        <h3>Extraction Comparison</h3>
        <div className="comp-actions">
          {/* Form/File Selector Dropdown */}
          {splitFiles.length > 0 && (
            <div className="file-selector">
              <select
                value={selectedFileId || ""}
                onChange={handleFileChange}
                className="file-dropdown"
                disabled={loadingFiles}
              >
                {splitFiles.map((file) => (
                  <option key={file._id} value={file._id}>
                    {file.formType || file.filename}{" "}
                    {file.hasExtraction ? "✓" : ""}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="dropdown-icon" />
            </div>
          )}
          {splitFiles.length === 0 && !loadingFiles && (
            <span className="no-files-badge">No split forms</span>
          )}
          {loadingFiles && (
            <span className="loading-files">
              <Loader size={14} className="spin" /> Loading forms...
            </span>
          )}
          <div className="view-toggle">
            {/* <button className={viewMode === "mapped" ? "active" : ""} onClick={() => setViewMode("mapped")}>Mapped View</button>
            <button className={viewMode === "raw" ? "active" : ""} onClick={() => setViewMode("raw")}>Raw View</button> */}
          </div>
          <button
            className="view-raw-json-btn"
            onClick={() => setShowRawJson(true)}
          >
            <Code size={16} /> JSON
          </button>
          <button
            className="view-raw-json-btn"
            onClick={fetchProviderResponse}
            disabled={providerLoading}
          >
            <Code size={16} />{" "}
            {providerLoading ? "Loading..." : "Provider JSON"}
          </button>
          <button className="view-pdf-btn" onClick={fetchPdfForViewing}>
            <Eye size={16} /> PDF
          </button>
          {autoRefresh && (
            <span className="auto-badge">
              <Loader size={14} className="spin" /> Auto
            </span>
          )}
          <button
            className="refresh-btn"
            onClick={() =>
              selectedFileId
                ? fetchComparisonForFile(selectedFileId)
                : fetchComparison()
            }
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Selected Form Info Banner */}
      {selectedFileId && comparison?.formType && (
        <div className="selected-form-banner">
          <span className="form-type-label">Selected Form:</span>
          <span className="form-type-value">{comparison.formType}</span>
          {comparison.filename && (
            <span className="form-filename">({comparison.filename})</span>
          )}
        </div>
      )}

      {/* GLIB Disabled Banner - Commented Out */}
      {/* {!enableGLIB ? (
        <div className="glib-disabled-banner">
          <AlertCircle size={18} className="warning" />
          <span>GLIB Extraction Disabled</span>
          <p>
            Showing local extraction data only. Enable GLIB in settings to see
            comparison data.
          </p>
        </div>
      ) : null} */}

      {enableGLIB && (
        <div className="status-row">
          <div className="status-box glib">
            {comparison?.glib?.status === "completed" ? (
              <CheckCircle size={18} className="success" />
            ) : comparison?.glib?.status === "processing" ? (
              <Loader size={18} className="spin" />
            ) : (
              <AlertCircle size={18} className="warning" />
            )}
            <span>
              GLIB: {comparison?.glib?.status?.toUpperCase() || "N/A"}
            </span>
            {comparison?.glib?.links?.reviewPortalLink && (
              <a
                href={comparison.glib.links.reviewPortalLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink size={14} />
              </a>
            )}
            {/* Refresh button for re-fetching GLIB data */}
            <button
              className="refresh-glib-btn"
              onClick={refreshGlibData}
              disabled={refreshingGlib}
              title="Refresh GLIB data from portal"
            >
              {refreshingGlib ? (
                <Loader size={14} className="spin" />
              ) : (
                <RefreshCw size={14} />
              )}
            </button>
          </div>
          <div className="status-box gemma">
            {comparison?.local?.status === "completed" ? (
              <CheckCircle size={18} className="success" />
            ) : (
              <Loader size={18} className="spin" />
            )}
            <span>
              {getProviderDisplayName()}:{" "}
              {comparison?.local?.status?.toUpperCase() || "N/A"}
            </span>
          </div>
        </div>
      )}

      {enableGLIB && (
        <div className="comparison-stats">
          <div className="stat match">
            <CheckCircle size={14} /> {stats.matches} Matches
          </div>
          <div className="stat mismatch">
            <XCircle size={14} /> {stats.mismatches} Mismatches
          </div>
          <div className="stat glib-only">
            <AlertCircle size={14} /> {stats.glibOnly} GLIB Only
          </div>
          <div className="stat gemma-only">
            <AlertCircle size={14} /> {stats.gemmaOnly} Gemma Only
          </div>
        </div>
      )}

      <div className="comparison-search">
        <Search size={10} className="search-icon" />
        <input
          type="text"
          placeholder="Search fields..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button className="clear-search" onClick={() => setSearchTerm("")}>
            ×
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <div
          className="unified-comparison-table"
          style={{ flex: "0 0 55%", overflow: "auto", maxHeight: "75vh" }}
        >
          <table>
            <thead>
              <tr>
                <th className="field-col">Field Name</th>
                {enableGLIB && <th className="glib-col">GLIB Value</th>}
                <th className="gemma-col">{getProviderDisplayName()} Value</th>
                {enableGLIB && <th className="status-col">Status</th>}
              </tr>
            </thead>
            <tbody>
              {enableGLIB ? (
                filteredRows.length > 0 ? (
                  filteredRows.map((row, idx) => (
                    <tr key={idx} className={`row-${row.status}`}>
                      <td className="field-name">{row.display}</td>
                      <td className="glib-value">{row.glibValue}</td>
                      <td
                        className="gemma-value"
                        style={
                          validateField(row.display, row.gemmaValue) === false
                            ? { background: "#FFC7CE" }
                            : {}
                        }
                      >
                        {row.gemmaValue}
                        {validateField(row.display, row.gemmaValue) ===
                          false && (
                          <span style={{ marginLeft: 6, color: "#742A2A" }}>
                            ✗
                          </span>
                        )}
                      </td>
                      <td className="status-cell">
                        {row.status === "match" && (
                          <CheckCircle size={16} className="icon-match" />
                        )}
                        {row.status === "mismatch" && (
                          <XCircle size={16} className="icon-mismatch" />
                        )}
                        {row.status === "glib_only" && (
                          <span className="badge glib">GLIB</span>
                        )}
                        {row.status === "gemma_only" && (
                          <span className="badge gemma">
                            {getProviderBadgeName()}
                          </span>
                        )}
                        {row.status === "both_empty" && (
                          <span className="badge empty">Empty</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="no-data">
                      {searchTerm
                        ? "No matching fields"
                        : "No comparison data available"}
                    </td>
                  </tr>
                )
              ) : getLocalOnlyComparison().length > 0 ? (
                getLocalOnlyComparison()
                  .filter((row) => {
                    if (!searchTerm.trim()) return true;
                    const term = searchTerm.toLowerCase();
                    return (
                      row.display.toLowerCase().includes(term) ||
                      row.gemmaValue.toLowerCase().includes(term)
                    );
                  })
                  .map((row, idx) => (
                    <tr key={idx} className="row-gemma-only">
                      <td className="field-name">{row.display}</td>
                      <td
                        className="gemma-value"
                        style={
                          validateField(row.display, row.gemmaValue) === false
                            ? { background: "#FFC7CE" }
                            : {}
                        }
                      >
                        {row.gemmaValue}
                        {validateField(row.display, row.gemmaValue) ===
                          false && (
                          <span style={{ marginLeft: 6, color: "#742A2A" }}>
                            ✗
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan={2} className="no-data">
                    {searchTerm
                      ? "No matching fields"
                      : "No extraction data available"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {pdfUrl && (
          <div
            style={{
              flex: "0 0 43%",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              overflow: "hidden",
              height: "75vh",
              position: "sticky",
              top: 80,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "6px 12px",
                background: "#f7fafc",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 13 }}>📄 Document</span>
            </div>
            <iframe
              src={pdfUrl}
              title="PDF Viewer"
              style={{
                width: "100%",
                height: "calc(100% - 34px)",
                border: "none",
              }}
            />
          </div>
        )}
      </div>

      {/* PDF Modal - opens when PDF button clicked */}
      {showPdfViewer && pdfUrl && (
        <div
          className="raw-json-modal-overlay"
          onClick={() => setShowPdfViewer(false)}
        >
          <div
            className="raw-json-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ width: "80%", height: "90vh" }}
          >
            <div className="raw-json-modal-header">
              <h3>PDF Document</h3>
              <button
                className="close-btn"
                onClick={() => setShowPdfViewer(false)}
              >
                <X size={24} />
              </button>
            </div>
            <iframe
              src={pdfUrl}
              title="PDF Viewer"
              style={{
                width: "100%",
                height: "calc(100% - 50px)",
                border: "none",
              }}
            />
          </div>
        </div>
      )}

      {showRawJson && (
        <div
          className="raw-json-modal-overlay"
          onClick={() => setShowRawJson(false)}
        >
          <div className="raw-json-modal" onClick={(e) => e.stopPropagation()}>
            <div className="raw-json-modal-header">
              <h3>Raw JSON {enableGLIB ? "Comparison" : "Extraction"}</h3>
              <button
                className="close-btn"
                onClick={() => setShowRawJson(false)}
              >
                <X size={24} />
              </button>
            </div>
            <div className="raw-json-container">
              {enableGLIB && (
                <div className="raw-json-section">
                  <div className="raw-json-header">
                    <h4>GLIB Portal (Raw Response)</h4>
                    <button
                      className={`copy-btn ${copiedTab === "glib" ? "copied" : ""}`}
                      onClick={() =>
                        copyToClipboard(
                          JSON.stringify(
                            comparison?.glib?.rawData ||
                              comparison?.glib?.data ||
                              {},
                            null,
                            2,
                          ),
                          "glib",
                        )
                      }
                    >
                      <Copy size={16} />
                      {copiedTab === "glib" ? "Copied!" : "Copy"}
                    </button>
                  </div>
                  <pre className="raw-json-content">
                    {JSON.stringify(
                      comparison?.glib?.rawData || comparison?.glib?.data || {},
                      null,
                      2,
                    )}
                  </pre>
                </div>
              )}
              <div className="raw-json-section">
                <div className="raw-json-header">
                  <h4>{enableGLIB ? "Gemma 4 31B" : "Local Extraction"}</h4>
                  <button
                    className={`copy-btn ${copiedTab === "gemma" ? "copied" : ""}`}
                    onClick={() =>
                      copyToClipboard(
                        JSON.stringify(comparison?.local?.data || {}, null, 2),
                        "gemma",
                      )
                    }
                  >
                    <Copy size={16} />
                    {copiedTab === "gemma" ? "Copied!" : "Copy"}
                  </button>
                </div>
                <pre className="raw-json-content">
                  {JSON.stringify(comparison?.local?.data || {}, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {showProviderJson && providerResponse && (
        <div
          className="raw-json-modal-overlay"
          onClick={() => setShowProviderJson(false)}
        >
          <div className="raw-json-modal" onClick={(e) => e.stopPropagation()}>
            <div className="raw-json-modal-header">
              <h3>Provider Response Comparison</h3>
              <button
                className="close-btn"
                onClick={() => setShowProviderJson(false)}
              >
                <X size={24} />
              </button>
            </div>
            <div className="raw-json-container">
              {providerResponse.with_template && (
                <div className="raw-json-section">
                  <div className="raw-json-header">
                    <h4>
                      With Template (
                      {
                        Object.keys(
                          providerResponse.with_template.extractedData || {},
                        ).length
                      }{" "}
                      fields)
                    </h4>
                    <button
                      className={`copy-btn ${copiedTab === "with_template" ? "copied" : ""}`}
                      onClick={() =>
                        copyToClipboard(
                          JSON.stringify(
                            providerResponse.with_template.extractedData || {},
                            null,
                            2,
                          ),
                          "with_template",
                        )
                      }
                    >
                      <Copy size={16} />
                      {copiedTab === "with_template" ? "Copied!" : "Copy"}
                    </button>
                  </div>
                  <pre className="raw-json-content">
                    {JSON.stringify(
                      providerResponse.with_template.extractedData || {},
                      null,
                      2,
                    )}
                  </pre>
                </div>
              )}
              {providerResponse.without_template && (
                <div className="raw-json-section">
                  <div className="raw-json-header">
                    <h4>
                      Without Template (
                      {
                        Object.keys(
                          providerResponse.without_template.extractedData || {},
                        ).length
                      }{" "}
                      fields)
                    </h4>
                    <button
                      className={`copy-btn ${copiedTab === "without_template" ? "copied" : ""}`}
                      onClick={() =>
                        copyToClipboard(
                          JSON.stringify(
                            providerResponse.without_template.extractedData ||
                              {},
                            null,
                            2,
                          ),
                          "without_template",
                        )
                      }
                    >
                      <Copy size={16} />
                      {copiedTab === "without_template" ? "Copied!" : "Copy"}
                    </button>
                  </div>
                  <pre className="raw-json-content">
                    {JSON.stringify(
                      providerResponse.without_template.extractedData || {},
                      null,
                      2,
                    )}
                  </pre>
                </div>
              )}
              {!providerResponse.with_template &&
                !providerResponse.without_template && (
                  <p style={{ padding: "20px", textAlign: "center" }}>
                    No provider response data available
                  </p>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtractionComparison;
